import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, AlertCircle, Check, ChevronDown, ChevronUp,
  Info, Shield
} from 'lucide-react';

// PAN validation regex: 5 letters + 4 digits + 1 letter
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

// Validate PAN format
const validatePAN = (pan) => {
  if (!pan) return { valid: false, error: 'PAN is required' };
  const upperPan = pan.toUpperCase().trim();
  if (upperPan.length !== 10) {
    return { valid: false, error: 'PAN must be exactly 10 characters' };
  }
  if (!PAN_REGEX.test(upperPan)) {
    return { valid: false, error: 'Invalid PAN format. Format: ABCDE1234F' };
  }
  return { valid: true, error: null };
};

const HotelGuestForm = ({
  guests = [],
  onGuestsChange,
  roomInfo = [],
  panRequired = false,
  passportRequired = false,
  onValidationChange
}) => {
  const [expandedGuest, setExpandedGuest] = useState(0); // First guest expanded by default
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Initialize guests if empty and ensure first guest is expanded
  React.useEffect(() => {
    if (guests.length === 0) {
      // Create default guest based on room info
      const totalAdults = roomInfo.reduce((sum, room) => sum + (room.adults || 2), 0) || 2;
      const initialGuests = Array.from({ length: totalAdults }, (_, i) => ({
        id: i,
        title: 'Mr',
        firstName: '',
        lastName: '',
        pan: '',
        isLead: i === 0
      }));
      onGuestsChange(initialGuests);
    }
    // Ensure first guest is expanded when guests load
    if (guests.length > 0 && expandedGuest === -1) {
      setExpandedGuest(0);
    }
  }, [guests.length, roomInfo, onGuestsChange, expandedGuest]);

  // Validate all guests
  const validateGuests = useCallback(() => {
    const newErrors = {};
    let isValid = true;

    guests.forEach((guest, idx) => {
      const guestErrors = {};
      
      if (!guest.firstName?.trim()) {
        guestErrors.firstName = 'First name is required';
        isValid = false;
      }
      if (!guest.lastName?.trim()) {
        guestErrors.lastName = 'Last name is required';
        isValid = false;
      }
      
      // PAN validation only if required
      if (panRequired) {
        const panValidation = validatePAN(guest.pan);
        if (!panValidation.valid) {
          guestErrors.pan = panValidation.error;
          isValid = false;
        }
      }

      if (Object.keys(guestErrors).length > 0) {
        newErrors[idx] = guestErrors;
      }
    });

    setErrors(newErrors);
    onValidationChange?.(isValid, guests);
    return isValid;
  }, [guests, panRequired, onValidationChange]);

  // Validate on guest changes
  React.useEffect(() => {
    validateGuests();
  }, [guests, validateGuests]);

  const handleGuestChange = (index, field, value) => {
    const updatedGuests = [...guests];
    
    // Handle PAN - uppercase and trim
    if (field === 'pan') {
      value = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    }
    
    updatedGuests[index] = {
      ...updatedGuests[index],
      [field]: value
    };
    
    onGuestsChange(updatedGuests);
    
    // Mark as touched
    setTouched(prev => ({
      ...prev,
      [`${index}-${field}`]: true
    }));
  };

  const handleBlur = (index, field) => {
    setTouched(prev => ({
      ...prev,
      [`${index}-${field}`]: true
    }));
    validateGuests();
  };

  const getFieldError = (index, field) => {
    if (!touched[`${index}-${field}`]) return null;
    return errors[index]?.[field];
  };

  return (
    <div className="space-y-4" data-testid="hotel-guest-form">
      {/* PAN Notice */}
      {panRequired && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3"
        >
          <Shield className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-300 text-sm font-semibold">PAN Required</p>
            <p className="text-amber-200/80 text-xs mt-1">
              As per government regulations, PAN (Permanent Account Number) is mandatory for this booking.
              Your PAN is securely transmitted and never stored.
            </p>
          </div>
        </motion.div>
      )}

      {/* Guest Forms */}
      {guests.map((guest, index) => (
        <motion.div
          key={guest.id || index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="bg-[#121228] rounded-xl border border-white/10 overflow-hidden"
        >
          {/* Guest Header */}
          <button
            onClick={() => setExpandedGuest(expandedGuest === index ? -1 : index)}
            className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
            data-testid={`guest-header-${index}`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                guest.isLead ? 'bg-emerald-500/20' : 'bg-white/10'
              }`}>
                <User className={`w-5 h-5 ${guest.isLead ? 'text-emerald-400' : 'text-white/60'}`} />
              </div>
              <div className="text-left">
                <p className="text-white font-medium">
                  {guest.firstName && guest.lastName 
                    ? `${guest.title} ${guest.firstName} ${guest.lastName}`
                    : `Guest ${index + 1}`}
                  {guest.isLead && (
                    <span className="ml-2 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
                      Lead Guest
                    </span>
                  )}
                </p>
                <p className="text-gray-400 text-sm">
                  {errors[index] ? (
                    <span className="text-red-400">Complete required fields</span>
                  ) : guest.firstName ? (
                    <span className="text-green-400 flex items-center gap-1">
                      <Check className="w-3 h-3" /> Details complete
                    </span>
                  ) : (
                    'Click to enter details'
                  )}
                </p>
              </div>
            </div>
            {expandedGuest === index ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {/* Guest Form Fields */}
          <AnimatePresence>
            {expandedGuest === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-white/10"
              >
                <div className="p-4 space-y-4 bg-[#0a0a1a]">
                  {/* Name Row */}
                  <div className="grid grid-cols-3 gap-3">
                    {/* Title */}
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">Title</label>
                      <select
                        value={guest.title || 'Mr'}
                        onChange={(e) => handleGuestChange(index, 'title', e.target.value)}
                        className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                        data-testid={`guest-title-${index}`}
                      >
                        <option value="Mr">Mr</option>
                        <option value="Mrs">Mrs</option>
                        <option value="Ms">Ms</option>
                        <option value="Miss">Miss</option>
                        <option value="Master">Master</option>
                      </select>
                    </div>

                    {/* First Name */}
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">
                        First Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={guest.firstName || ''}
                        onChange={(e) => handleGuestChange(index, 'firstName', e.target.value)}
                        onBlur={() => handleBlur(index, 'firstName')}
                        placeholder="JOHN"
                        className={`w-full px-3 py-2.5 rounded-lg bg-white/5 border text-white text-sm placeholder-white/30 focus:outline-none ${
                          getFieldError(index, 'firstName')
                            ? 'border-red-500/50 focus:border-red-500'
                            : 'border-white/10 focus:border-emerald-500/50'
                        }`}
                        data-testid={`guest-firstname-${index}`}
                      />
                      {getFieldError(index, 'firstName') && (
                        <p className="mt-1 text-xs text-red-400">{getFieldError(index, 'firstName')}</p>
                      )}
                    </div>

                    {/* Last Name */}
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">
                        Last Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={guest.lastName || ''}
                        onChange={(e) => handleGuestChange(index, 'lastName', e.target.value)}
                        onBlur={() => handleBlur(index, 'lastName')}
                        placeholder="DOE"
                        className={`w-full px-3 py-2.5 rounded-lg bg-white/5 border text-white text-sm placeholder-white/30 focus:outline-none ${
                          getFieldError(index, 'lastName')
                            ? 'border-red-500/50 focus:border-red-500'
                            : 'border-white/10 focus:border-emerald-500/50'
                        }`}
                        data-testid={`guest-lastname-${index}`}
                      />
                      {getFieldError(index, 'lastName') && (
                        <p className="mt-1 text-xs text-red-400">{getFieldError(index, 'lastName')}</p>
                      )}
                    </div>
                  </div>

                  {/* PAN Field - Only show if required */}
                  {panRequired && (
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">
                        PAN Number <span className="text-red-400">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={guest.pan || ''}
                          onChange={(e) => handleGuestChange(index, 'pan', e.target.value)}
                          onBlur={() => handleBlur(index, 'pan')}
                          placeholder="ABCDE1234F"
                          maxLength={10}
                          className={`w-full px-3 py-2.5 rounded-lg bg-white/5 border text-white text-sm placeholder-white/30 focus:outline-none uppercase tracking-wider font-mono ${
                            getFieldError(index, 'pan')
                              ? 'border-red-500/50 focus:border-red-500'
                              : guest.pan && validatePAN(guest.pan).valid
                                ? 'border-green-500/50'
                                : 'border-white/10 focus:border-emerald-500/50'
                          }`}
                          data-testid={`guest-pan-${index}`}
                          autoComplete="off"
                        />
                        {guest.pan && validatePAN(guest.pan).valid && (
                          <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-400" />
                        )}
                      </div>
                      {getFieldError(index, 'pan') && (
                        <p className="mt-1 text-xs text-red-400">{getFieldError(index, 'pan')}</p>
                      )}
                      <p className="mt-1.5 text-xs text-gray-500 flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        Format: 5 letters + 4 digits + 1 letter (e.g., ABCDE1234F)
                      </p>
                    </div>
                  )}

                  {/* Security Notice */}
                  {panRequired && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                      <Shield className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-blue-300">
                        Your PAN is encrypted and transmitted securely. It is used only for compliance 
                        verification and is not stored in our systems.
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}

      {/* Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-300">
            Please complete all required guest information{panRequired ? ', including PAN' : ''}.
          </p>
        </div>
      )}
    </div>
  );
};

export default HotelGuestForm;
